import { createScheduleSchema, createTriggerSchema } from "@zworks/console-contracts";

/**
 * The nine starting points for a first automation.
 *
 * A template is not a rule. It is a named shape with every field the create endpoint needs, so a
 * person can start from "nightly reconciliation" rather than from an empty form, and it is checked
 * against the same schema the form submits to. A template that produced an invalid draft would fail
 * at the endpoint with a message the person did not cause.
 */
export type TemplateKind = "schedule" | "trigger";

export interface AutomationTemplate {
  id: string;
  title: string;
  /** What it does, in the words the reader uses. */
  summary: string;
  kind: TemplateKind;
  /** The event a trigger listens for; empty for a schedule. */
  event: string;
  /** The cron a schedule runs on; empty for a trigger. */
  cron: string;
  /** What the run should do, prefilled so the draft reads as an intention. */
  intent: string;
  /**
   * Whether the template needs a repository, which a project may not have connected. A template
   * that cannot be filled in is shown disabled rather than producing a draft that fails on submit.
   */
  needsRepository: boolean;
}

const nightly = "0 2 * * *";

export const automationTemplates: AutomationTemplate[] = [
  {
    id: "nightly-reconciliation",
    title: "Nightly reconciliation",
    summary:
      "Check that the ledger, the outbox, and the work items agree, and report the differences.",
    kind: "schedule",
    event: "",
    cron: nightly,
    intent: "Reconcile the project's records and report anything that disagrees.",
    needsRepository: false,
  },
  {
    id: "review-every-pull-request",
    title: "Review every pull request",
    summary:
      "Read each new pull request, leave a review comment, and flag anything that needs a person.",
    kind: "trigger",
    event: "pull_request.opened",
    cron: "",
    intent: "Review the pull request for correctness, tests, and risk, and say what you found.",
    needsRepository: true,
  },
  {
    id: "weekly-dependency-audit",
    title: "Weekly dependency audit",
    summary:
      "Check the lockfiles for advisories and outdated packages, and open one issue with the findings.",
    kind: "schedule",
    event: "",
    cron: "0 6 * * 1",
    intent:
      "Audit dependencies for advisories and outdated packages, and report them in one place.",
    needsRepository: true,
  },
  {
    id: "triage-a-failed-run",
    title: "Triage a failed run",
    summary:
      "When a check fails, read the trace and say whether it is a product bug, a test issue, or the environment.",
    kind: "trigger",
    event: "check_run.failed",
    cron: "",
    intent: "Read the failing run's trace and evidence, and classify the failure with its reason.",
    needsRepository: true,
  },
  {
    id: "stale-pull-request-nudge",
    title: "Stale pull request nudge",
    summary: "Find pull requests that have not moved for a week and tell their author.",
    kind: "schedule",
    event: "",
    cron: "0 9 * * 1",
    intent: "Find pull requests with no activity for seven days and notify their authors.",
    needsRepository: true,
  },
  {
    id: "spend-alert",
    title: "Spend alert",
    summary: "When a project passes 80% of its monthly cap, say so before the cap is reached.",
    kind: "trigger",
    event: "spend.threshold",
    cron: "",
    intent: "Report when the project reaches 80% of its monthly spend cap, and what is driving it.",
    needsRepository: false,
  },
  {
    id: "release-checklist",
    title: "Release checklist",
    summary: "Walk the release steps and report which are done, with evidence for each.",
    kind: "schedule",
    event: "",
    cron: "0 10 * * 4",
    intent:
      "Walk the release checklist, gather evidence for each step, and report what is outstanding.",
    needsRepository: true,
  },
  {
    id: "connection-health",
    title: "Connection health",
    summary:
      "Check every integration connection and report the ones that are failing or about to expire.",
    kind: "schedule",
    event: "",
    cron: nightly,
    intent: "Check each connection's health and expiry, and report the ones needing action.",
    needsRepository: false,
  },
  {
    id: "quarantine-flaky-tests",
    title: "Quarantine flaky tests",
    summary: "Find tests that both passed and failed this week and propose a quarantine for each.",
    kind: "schedule",
    event: "",
    cron: "0 7 * * 1",
    intent:
      "Find tests with mixed results this week and propose quarantining each, with its evidence.",
    needsRepository: true,
  },
];

/** The fields a draft needs from the person, beyond the template. */
export interface DraftFields {
  name: string;
  flowId: string;
  flowVersion: number;
  flowVersionId: string;
  /** The branch the run works on. */
  branch: string;
  /** The monthly cap, in credits, as the form collects it. */
  creditCap: number;
  repositoryRef?: string;
  signingSecret?: string;
}

export interface Draft {
  kind: TemplateKind;
  /** The body the create endpoint takes, exactly as the form would submit it. */
  body: unknown;
  /** The problem with this template in this project, when it cannot be filled. */
  problem?: string;
}

/**
 * Builds the create request for one template.
 *
 * It refuses rather than filling a gap: a repository template in a project with no repository would
 * otherwise produce a draft that fails at the endpoint, and the person would read a validation error
 * they did not cause.
 */
export function buildDraft(template: AutomationTemplate, fields: DraftFields): Draft {
  if (fields.name.trim() === "") {
    return { kind: template.kind, body: null, problem: "Give the automation a name." };
  }
  if (template.needsRepository && !fields.repositoryRef) {
    return {
      kind: template.kind,
      body: null,
      problem: "This template needs a repository, and this project has none connected.",
    };
  }
  const run = {
    intent: template.intent,
    branch: fields.branch.trim(),
    creditCap: fields.creditCap,
  };
  if (template.kind === "schedule") {
    const body = {
      name: fields.name.trim(),
      cron: template.cron,
      flowVersion: fields.flowVersion,
      flowId: fields.flowId,
      flowVersionId: fields.flowVersionId,
      run,
    };
    const parsed = createScheduleSchema.safeParse(body);
    return parsed.success
      ? { kind: "schedule", body }
      : { kind: "schedule", body: null, problem: firstIssue(parsed.error.issues) };
  }
  const body = {
    source: "repository-event",
    event: template.event,
    flowVersion: fields.flowVersion,
    flowId: fields.flowId,
    flowVersionId: fields.flowVersionId,
    run,
    repositoryRef: fields.repositoryRef,
    signingSecret: fields.signingSecret,
  };
  const parsed = createTriggerSchema.safeParse(body);
  return parsed.success
    ? { kind: "trigger", body }
    : { kind: "trigger", body: null, problem: firstIssue(parsed.error.issues) };
}

/**
 * The templates a project can start from, with the ones it cannot fill marked.
 *
 * A template is shown disabled rather than hidden: a person who has not connected a repository should
 * see that review-every-pull-request exists and needs one, not wonder why the list is short.
 */
export function templatesFor(
  hasRepository: boolean,
): Array<AutomationTemplate & { available: boolean; reason?: string }> {
  return automationTemplates.map((template) => ({
    ...template,
    available: !template.needsRepository || hasRepository,
    reason:
      template.needsRepository && !hasRepository
        ? "Connect a repository to use this template."
        : undefined,
  }));
}

function firstIssue(
  issues: ReadonlyArray<{ path: ReadonlyArray<PropertyKey>; message: string }>,
): string {
  const issue = issues[0];
  if (!issue) return "The draft is not valid.";
  const path = issue.path.map(String).join(".");
  const where = path.length > 0 ? `${path}: ` : "";
  return `${where}${issue.message}`;
}
